import React, {useEffect, useRef} from 'react'
import AdyenCheckout from '@adyen/adyen-web'
import '@adyen/adyen-web/dist/adyen.css'
import {Spinner, Flex} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import {getCurrencyValueForApi} from '../../../adyenOverrides/utils/parsers.mjs'
import {AdyenPaymentsService} from '../../../adyenOverrides/services/payments'
import {AdyenShippingMethodsService} from '../../../adyenOverrides/services/shipping-methods'
import {AdyenShippingAddressService} from '../../../adyenOverrides/services/shipping-address'
import {PAYMENT_METHODS} from '../../../adyenOverrides/utils/constants.mjs'
import {getApplePayStyle} from './index.style'
import useAdyenExpressCheckout from '../../../adyenOverrides/hooks/useAdyenExpressCheckout'
import {
    getPayableAmount,
    getSelectedDeliveryShippingMethodIds,
    updateBasketOnCheckout
} from '../../../adyenOverrides/utils/orderUtils'
import {isEmpty} from '../../../../../utills/objectUtils'

const PAYMENT_METHOD = PAYMENT_METHODS.APPLE_PAY
const phoneRegEx = /^(?=(?:\D*\d){10,11}\D*$)[+\-\s().\d]+$/

export const getApplePaymentMethodConfig = (paymentMethodsResponse) => {
    const applePayPaymentMethod = paymentMethodsResponse?.paymentMethods?.find(
        (pm) => pm.type === PAYMENT_METHOD
    )
    return applePayPaymentMethod?.configuration || null
}

export const createError = (form, field, msg) => {
    return new ApplePayError(form, field, msg)
}

export const validateAddressFields = (form, formValue) => {
    let errors = []
    if (isEmpty(formValue.addressLines?.[0])) {
        errors.push(createError(form, 'addressLines', 'Street is Required'))
    }
    if (isEmpty(formValue.locality)) {
        errors.push(createError(form, 'locality', 'City is Required'))
    }
    if (isEmpty(formValue.administrativeArea)) {
        errors.push(createError(form, 'administrativeArea', 'State is Required'))
    }
    if (isEmpty(formValue.postalCode)) {
        errors.push(createError(form, 'postalCode', 'Postal Code is Required'))
    }
    if (isEmpty(formValue.countryCode)) {
        errors.push(createError(form, 'countryCode', 'Country is Required'))
    }
    return errors
}

export const validateName = (form, formValue) => {
    let errors = []
    if (isEmpty(formValue.givenName)) {
        errors.push(createError(form, 'name', 'First Name is Required'))
    }
    if (isEmpty(formValue.familyName)) {
        errors.push(createError(form, 'name', 'Last Name is Required'))
    }
    return errors
}

export const validateProfile = (form, formValue) => {
    let errors = [...validateName(form, formValue)]
    if (isEmpty(formValue.emailAddress)) {
        errors.push(createError(form, 'emailAddress', 'EmailAddress is Required'))
    }
    if (isEmpty(formValue.phoneNumber)) {
        errors.push(createError(form, 'phoneNumber', 'PhoneNumber is Required'))
    }
    return errors
}

export const getCustomerShippingDetails = (shippingContact) => {
    return {
        deliveryAddress: {
            city: shippingContact.locality,
            country: shippingContact.countryCode,
            houseNumberOrName:
                shippingContact.addressLines?.length > 1 ? shippingContact.addressLines[1] : '',
            postalCode: shippingContact.postalCode,
            stateOrProvince: shippingContact.administrativeArea,
            street: shippingContact.addressLines?.[0],
            firstName: shippingContact.givenName,
            lastName: shippingContact.familyName
        },
        profile: {
            email: shippingContact.emailAddress,
            phone: shippingContact.phoneNumber
        }
    }
}

export const getCustomerBillingDetails = (billingContact) => {
    return {
        billingAddress: {
            city: billingContact.locality,
            country: billingContact.countryCode,
            houseNumberOrName:
                billingContact?.addressLines?.length > 1 ? billingContact.addressLines[1] : '',
            postalCode: billingContact.postalCode,
            stateOrProvince: billingContact.administrativeArea,
            street: billingContact.addressLines?.[0],
            firstName: billingContact.givenName,
            lastName: billingContact.familyName
        }
    }
}

export const getShippingMethods = (basket, shippingMethods) => {
    if (basket?.c_isBopisBossGCOnly || basket?.c_isStorePickup) {
        const {id, description, name, price} = basket.shipments[0].shippingMethod
        return [
            {
                label: name,
                detail: description,
                identifier: id,
                amount: parseFloat(price).toFixed(2)
            }
        ]
    } else {
        return shippingMethods?.map((sm) => ({
            label: sm.name,
            detail: sm.description,
            identifier: sm.id,
            amount: parseFloat(sm.price).toFixed(2)
        }))
    }
}

export const getAppleButtonConfig = (
    authToken,
    site,
    basket,
    shippingMethods,
    adyenPaymentMethods,
    navigate,
    fetchShippingMethods,
    applePayStateDataRef
) => {
    const applePayConfig = getApplePaymentMethodConfig(adyenPaymentMethods)
    let applePayAmount = getPayableAmount(basket)
    const shippingRequiredFields = ['name', 'email', 'phone']
    const buttonConfig = {
        showPayButton: true,
        buttonType: 'order',
        isExpress: true,
        configuration: applePayConfig,
        countryCode: 'US',
        amount: {
            value: getCurrencyValueForApi(basket.orderTotal, basket.currency),
            currency: basket.currency
        },
        requiredShippingContactFields: [
            ...(!basket.c_isStorePickup ? ['postalAddress'] : []),
            ...shippingRequiredFields
        ],
        requiredBillingContactFields: ['postalAddress'],
        shippingMethods: getShippingMethods(basket, shippingMethods),
        // Fires when the BIN (first 6-8 digits) of the Apple Pay card is available.
        onBinValue: (binData) => {
            if (applePayStateDataRef) {
                applePayStateDataRef.current = {
                    ...applePayStateDataRef.current,
                    cardBIN: binData?.binValue || '',
                    binValueData: binData
                }
            }
        },
        // Fires when an Apple Pay card field becomes valid.
        // Captures endDigits and issuerBin when available.
        onFieldValid: (fieldData) => {
            if (applePayStateDataRef) {
                applePayStateDataRef.current = {
                    ...applePayStateDataRef.current,
                    endDigits:
                        fieldData?.endDigits || applePayStateDataRef.current?.endDigits || '',
                    issuerBin:
                        fieldData?.issuerBin || applePayStateDataRef.current?.issuerBin || '',
                    fieldValidData: fieldData
                }
            }
        },
        onAuthorized: async (resolve, reject, event) => {
            try {
                const {shippingContact, billingContact, token} = event.payment
                const billingNewTotal = {
                    type: 'final',
                    label: applePayConfig.merchantName,
                    amount: `${applePayAmount}`
                }
                const validations = [
                    {
                        result: validateProfile('shippingContactInvalid', shippingContact),
                        label: 'profileValidationResult'
                    },
                    {
                        result: validateAddressFields('shippingContactInvalid', shippingContact),
                        label: 'shippingValidationResult'
                    },
                    {
                        result: validateName('billingContactInvalid', billingContact),
                        label: 'billingProfileValidationResult'
                    },
                    {
                        result: validateAddressFields('billingContactInvalid', billingContact),
                        label: 'billingValidationResult'
                    }
                ]
                for (let index = 0; index < validations.length; index++) {
                    let validation = validations[index]
                    if (validation.result.length > 0) {
                        resolve({
                            newTotal: billingNewTotal,
                            errors: validation.result
                        })
                        return
                    }
                }

                const phoneNumber = billingContact.phoneNumber || shippingContact.phoneNumber
                if (!phoneRegEx.test(phoneNumber)) {
                    const updateBillingPhoneError = {
                        // Get the total from the application state.
                        newTotal: billingNewTotal,
                        errors: [
                            new ApplePayError(
                                'shippingContactInvalid',
                                'phoneNumber',
                                'Invalid phone number. Please enter a valid US phone number.'
                            )
                        ]
                    }
                    resolve(updateBillingPhoneError)
                    return
                }
                if (billingContact.countryCode !== 'US') {
                    const updateCountryError = {
                        // Get the total from the application state.
                        newTotal: billingNewTotal,
                        errors: [
                            new ApplePayError(
                                'billingContactInvalid',
                                'countryCode',
                                'Please add a US billing address only.'
                            )
                        ]
                    }
                    resolve(updateCountryError)
                    return
                }
                // Read latest BIN data from the ref (populated via onBinValue / onFieldValid)
                const latestStateData = applePayStateDataRef?.current || {}
                // Extract card information from Apple Pay token
                // Apple Pay provides card details in the payment token, not through onBinValue/onFieldValid
                const applePayCardInfo = token?.paymentMethod || {}
                const applePayCardNetwork = applePayCardInfo?.network || '' // e.g., "Visa", "Mastercard"
                const applePayDisplayName = applePayCardInfo?.displayName || '' // e.g., "Visa 1234"
                // Extract last 4 digits from displayName (format is typically "Network ####")
                const endDigitsMatch = applePayDisplayName.match(/\d{4}$/)
                const endDigits = endDigitsMatch ? endDigitsMatch[0] : ''

                // Build Signifyd session ID from the script tag (same logic as Google Pay)
                const sigSessionId =
                    document.getElementById('sig-api')?.dataset?.orderSessionId || null

                // Build c_signifydData payload mirroring the Google Pay pattern
                const signifydPayload = JSON.stringify({
                    cardBIN: applePayStateDataRef?.current?.cardBIN || null,
                    endDigits: endDigits || null,
                    holderName:
                        `${shippingContact.givenName || ''} ${
                            shippingContact.familyName || ''
                        }`.trim() || null,
                    cardNetwork: applePayCardNetwork,
                    paymentGateway: 'ApplePay_Express'
                })
                let custShippingDetails = getCustomerShippingDetails(shippingContact)
                if (basket?.c_isStorePickup) {
                    custShippingDetails.deliveryAddress = null
                }
                const state = {
                    data: {
                        paymentType: 'express',
                        paymentMethod: {
                            type: PAYMENT_METHOD,
                            applePayToken: token.paymentData
                        },
                        ...getCustomerBillingDetails(billingContact),
                        ...custShippingDetails
                    }
                }
                // Enrich the payment data with card info extracted from Apple Pay token
                // Note: Apple Pay doesn't trigger onBinValue/onFieldValid - card info comes from the token
                const enrichedData = {
                    ...state.data,
                    c_signifydData: signifydPayload
                }
                const adyenPaymentService = new AdyenPaymentsService(authToken, site)
                const paymentsResponse = await adyenPaymentService.submitPayment(
                    {
                        ...enrichedData,
                        origin: enrichedData.origin ? enrichedData.origin : window.location.origin
                    },
                    basket?.basketId,
                    basket?.customerInfo?.customerId
                )
                if (paymentsResponse?.isFinal && paymentsResponse?.isSuccessful) {
                    const finalPriceUpdate = {
                        newTotal: billingNewTotal
                    }
                    resolve(finalPriceUpdate)
                    navigate(`/checkout/confirmation/${paymentsResponse?.merchantReference}`)
                } else {
                    reject()
                }
            } catch (err) {
                reject()
            }
        },
        onSubmit: () => {},
        onShippingContactSelected: async (resolve, reject, event) => {
            try {
                if (basket?.c_isBopisBossGCOnly || basket?.c_isStorePickup) {
                    reject()
                } else {
                    const newOrderTotal = {
                        type: 'final',
                        label: applePayConfig.merchantName,
                        amount: `${applePayAmount}`
                    }
                    const {shippingContact} = event
                    const adyenShippingAddressService = new AdyenShippingAddressService(
                        authToken,
                        site
                    )
                    const adyenShippingMethodsService = new AdyenShippingMethodsService(
                        authToken,
                        site
                    )
                    const customerShippingDetails = getCustomerShippingDetails(shippingContact)
                    if (
                        customerShippingDetails &&
                        customerShippingDetails.deliveryAddress &&
                        customerShippingDetails.deliveryAddress.country !== 'US'
                    ) {
                        const updateCountryError = {
                            // Get the total from the application state.
                            newTotal: newOrderTotal,
                            errors: [
                                new ApplePayError(
                                    'shippingContactInvalid',
                                    'countryCode',
                                    'Cannot ship to the selected address. Please use a US shipping address only.'
                                )
                            ]
                        }
                        resolve(updateCountryError)
                        return
                    }
                    await adyenShippingAddressService.updateShippingAddress(
                        basket.basketId,
                        customerShippingDetails
                    )
                    const newShippingMethods = await fetchShippingMethods(
                        basket?.basketId,
                        site,
                        authToken
                    )
                    if (
                        !newShippingMethods?.applicableShippingMethods?.length ||
                        newShippingMethods?.applicableShippingMethods?.length === 0
                    ) {
                        reject()
                    } else {
                        const updatedResponse = await updateBasketOnCheckout(
                            basket?.basketId,
                            site,
                            authToken
                        )
                        const selectedDeliveryShippingMethods =
                            getSelectedDeliveryShippingMethodIds(updatedResponse)
                        const shippingMethodId =
                            selectedDeliveryShippingMethods.length > 0
                                ? selectedDeliveryShippingMethods[0].shippingMethod.id
                                : newShippingMethods.applicableShippingMethods[0].id
                        const response = await adyenShippingMethodsService.updateShippingMethod(
                            shippingMethodId,
                            basket.basketId
                        )
                        let finalPayableAmount = getPayableAmount(response)
                        buttonConfig.amount = {
                            value: getCurrencyValueForApi(finalPayableAmount, response.currency),
                            currency: response.currency
                        }
                        const finalPriceUpdate = {
                            newShippingMethods: newShippingMethods?.applicableShippingMethods?.map(
                                (sm) => ({
                                    label: sm.name,
                                    detail: sm.description,
                                    identifier: sm.id,
                                    amount: `${sm.price}`
                                })
                            ),
                            newTotal: {
                                type: 'final',
                                label: applePayConfig.merchantName,
                                amount: `${finalPayableAmount}`
                            }
                        }
                        resolve(finalPriceUpdate)
                    }
                }
            } catch (err) {
                reject()
            }
        },
        onShippingMethodSelected: async (resolve, reject, event) => {
            try {
                const {shippingMethod} = event
                const adyenShippingMethodsService = new AdyenShippingMethodsService(authToken, site)
                const response = await adyenShippingMethodsService.updateShippingMethod(
                    shippingMethod.identifier,
                    basket.basketId
                )
                if (response.error) {
                    reject()
                } else {
                    let finalPayableAmount = getPayableAmount(response)
                    buttonConfig.amount = {
                        value: getCurrencyValueForApi(finalPayableAmount, response.currency),
                        currency: response.currency
                    }
                    applePayAmount = finalPayableAmount
                    const applePayShippingMethodUpdate = {
                        newTotal: {
                            type: 'final',
                            label: applePayConfig.merchantName,
                            amount: `${applePayAmount}`
                        }
                    }
                    resolve(applePayShippingMethodUpdate)
                }
            } catch (err) {
                reject()
            }
        }
    }
    return buttonConfig
}

const ApplePayExpressComponent = (props) => {
    const {
        adyenEnvironment,
        adyenPaymentMethods,
        basket,
        locale,
        site,
        authToken,
        navigate,
        shippingMethods,
        fetchShippingMethods,
        isMiniCart
    } = useAdyenExpressCheckout()
    const paymentContainer = useRef(null)

    // Use a ref to always hold the latest BIN / field data without causing stale closures.
    const applePayStateDataRef = useRef({
        cardBIN: '',
        issuerBin: '',
        endDigits: '',
        binValueData: null,
        fieldValidData: null
    })

    useEffect(() => {
        const createCheckout = async () => {
            try {
                const shippingMethodsData =
                    shippingMethods ||
                    (props.shippingMethods
                        ? props.shippingMethods
                        : await fetchShippingMethods(basket?.basketId, site, authToken))
                const checkout = await AdyenCheckout({
                    environment: adyenEnvironment?.ADYEN_ENVIRONMENT,
                    clientKey: adyenEnvironment?.ADYEN_CLIENT_KEY,
                    locale: locale.id,
                    analytics: {
                        analyticsData: {
                            applicationInfo: adyenPaymentMethods?.applicationInfo
                        }
                    }
                })
                const appleButtonConfig = getAppleButtonConfig(
                    authToken,
                    site,
                    basket,
                    shippingMethodsData?.applicableShippingMethods,
                    adyenPaymentMethods,
                    navigate,
                    fetchShippingMethods,
                    applePayStateDataRef
                )
                const applePayButton = await checkout.create(PAYMENT_METHOD, appleButtonConfig)
                const isApplePayButtonAvailable = await applePayButton.isAvailable()
                if (isApplePayButtonAvailable && appleButtonConfig.configuration) {
                    applePayButton.mount(paymentContainer.current)
                }
            } catch (err) {
                // Error
            }
        }
        if (
            adyenEnvironment &&
            adyenPaymentMethods &&
            basket &&
            shippingMethods &&
            paymentContainer.current
        ) {
            createCheckout()
        }
    }, [adyenEnvironment, adyenPaymentMethods, shippingMethods])

    return (
        <>
            {props.showLoading && (
                <Flex align={'center'} justify={'center'}>
                    <Spinner size={'lg'} mt={4} />
                </Flex>
            )}
            <Flex
                justifyContent={'center'}
                sx={getApplePayStyle(isMiniCart)}
                ref={paymentContainer}
            ></Flex>
        </>
    )
}

ApplePayExpressComponent.propTypes = {
    showLoading: PropTypes.bool,
    shippingMethods: PropTypes.array
}

export default ApplePayExpressComponent
